// ✅ ALTERED ONLY FOR THEME (matches your “above theme”)
// - Orange header bar + white text
// - Page bg #F2F2F2 + inner white surfaces with soft blue border
// - Left orange accent stripe for Filters section + Student Details section
// - Inputs/autocomplete: soft blue border + orange focus
// - Buttons: primary orange, outlined blue
// - Tables: blue header, soft zebra rows, hover tint, subtle borders
// - No business/API logic changed (only styling + layout)
// - Small UI fixes: consistent sx usage, table wrappers, totals styling

import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  Autocomplete,
  LinearProgress,
  CircularProgress,
  Typography,
  Box,
  Divider,
  Alert,
  Snackbar,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { API_URL, API_UPLOAD_URL } from "./config";

const ReportStudentMarks = () => {
  const navigate = useNavigate();

  const [studentList, setStudentList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentDetails, setStudentDetails] = useState(null);
  const [marksBySemester, setMarksBySemester] = useState({});
  const [logincampusCode, setCampusCode] = useState("");

  const token = localStorage.getItem("auth");

  // --- NEW: dropdown filters + options ---
  const [filters, setFilters] = useState({
    SubCampusCode: [],
    DegreeCode: [],
    BranchCode: [],
    SemesterCode: [],
  });

  const [options, setOptions] = useState({
    SubCampusCode: [],
    DegreeCode: [],
    BranchCode: [],
    SemesterCode: [],
  });

  const [loadingLookups, setLoadingLookups] = useState(false);

  // --- NEW: students list based on dropdown filters ---
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [allFilteredStudents, setAllFilteredStudents] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [listError, setListError] = useState("");
  const [isCollapsed, setIsCollapsed] = useState(false);

  // optional messages
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const authHeader = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  // ✅ Theme constants (same as your other pages)
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_HOVER = "rgba(25,118,210,0.45)";
  const BORDER_FOCUS = "rgba(240,120,40,0.80)";
  const ROW_ALT = "rgba(25,118,210,0.06)";
  const ROW_HOVER = "rgba(25,118,210,0.10)";

  const pageWrapSx = { p: 2, backgroundColor: BG };
  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
  };
  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    flexWrap: "wrap",
  };
  const bodySx = { p: 2, backgroundColor: BG };

  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "white",
    border: `1px solid ${BORDER}`,
  };

  const sectionSx = {
    ...surfaceSx,
    p: 2,
    position: "relative",
    overflow: "hidden",
    "&:before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: 6,
      background: `linear-gradient(180deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
      opacity: 0.95,
    },
  };

  const inputSx = {
    "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_HOVER },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_FOCUS },
    "& .MuiInputLabel-root.Mui-focused": { color: THEME_ORANGE },
  };

  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
  };

  const outlinedBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    borderColor: "rgba(25,118,210,0.35)",
    color: "rgba(25,118,210,0.95)",
    "&:hover": {
      borderColor: "rgba(25,118,210,0.55)",
      backgroundColor: "rgba(25,118,210,0.04)",
    },
  };

  const tableWrapSx = {
    ...surfaceSx,
    overflow: "hidden",
    overflowX: "auto",     // ✅ allow left-right scroll
    overflowY: "hidden",
    WebkitOverflowScrolling: "touch", // ✅ smooth iOS scrolling
  };

  const theadRowSx = {
    backgroundColor: "rgba(25,118,210,0.95)",
    "& th": {
      color: "#fff",
      fontWeight: 900,
      borderBottom: "1px solid rgba(255,255,255,0.18)",
      whiteSpace: "nowrap",
      py: 1,
    },
  };

  const zebraRowSx = (idx) => ({
    backgroundColor: idx % 2 === 0 ? ROW_ALT : "#FFFFFF",
    "&:hover": { backgroundColor: ROW_HOVER },
    "& td": { borderBottom: "1px solid rgba(25,118,210,0.10)", py: 0.8 },
  });

  const subHeaderRowSx = {
    backgroundColor: "rgba(25,118,210,0.08)",
    "& td": {
      fontWeight: 900,
      color: "rgba(25,118,210,0.95)",
      borderBottom: "1px solid rgba(25,118,210,0.12)",
      py: 1,
    },
  };

  // Extract CampusCode from JWT
  useEffect(() => {
    if (token) {
      try {
        const decodedToken = JSON.parse(atob(token.split(".")[1]));
        const campusCode = decodedToken.campusCode || decodedToken.CampusCode;
        setCampusCode(campusCode || "");
      } catch (err) {
        console.error("Error decoding token:", err);
        setCampusCode("");
      }
    }
  }, [token]);

  // --- fetch dropdown lookups ---
  useEffect(() => {
    if (!logincampusCode) return;
    let cancelled = false;

    (async () => {
      try {
        setLoadingLookups(true);
        const { data } = await axios.get(`${API_URL}/api/lookups/all`, {
          ...authHeader,
          params: { CampusCode: logincampusCode },
        });

        if (cancelled) return;
        setOptions({
          SubCampusCode: data?.SubCampusCode || [],
          DegreeCode: data?.DegreeCode || [],
          BranchCode: data?.BranchCode || [],
          SemesterCode: (data?.SemesterCode || []).map(String),
        });
      } catch (e) {
        console.error("Failed to load dropdowns:", e);
      } finally {
        if (!cancelled) setLoadingLookups(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [logincampusCode, authHeader]);

  // Fetch student list for free-text search dropdown
  useEffect(() => {
    const fetchStudentList = async () => {
      if (searchTerm.length < 3 || !logincampusCode) {
        setStudentList([]);
        return;
      }

      try {
        const response = await axios.get(`${API_URL}/api/studentList`, {
          params: {
            search: encodeURIComponent(searchTerm),
            CampusCode: logincampusCode,
          },
          headers: { Authorization: `Bearer ${token}` },
        });
        setStudentList(response.data || []);
      } catch (error) {
        console.error("Error fetching student list:", error);
      }
    };

    const t = setTimeout(fetchStudentList, 300);
    return () => clearTimeout(t);
  }, [searchTerm, logincampusCode, token]);

  // Fetch student marks
  const fetchStudentMarks = async (studentID) => {
    try {
      const response = await axios.get(`${API_URL}/api/dataReportStudentMarks`, {
        params: {
          search: encodeURIComponent(studentID),
          CampusCode: logincampusCode,
        },
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = response.data;
      if (data) {
        setStudentDetails(data.studentDetails);
        setMarksBySemester(data.marksBySemester || {});
      } else {
        setStudentDetails(null);
        setMarksBySemester({});
      }
    } catch (error) {
      console.error("Error fetching student marks:", error);
      if (error.response && error.response.status === 401) {
        navigate("/");
      } else {
        setError("Failed to fetch student marks");
      }
    }
  };

  const handleSearch = () => {
    if (selectedStudent?.StudentID) {
      fetchStudentMarks(selectedStudent.StudentID);
    } else {
      setStudentDetails(null);
      setMarksBySemester({});
    }
  };

  // filter students by dropdowns
  const anyFilterSelected =
    (filters.SubCampusCode?.length || 0) > 0 ||
    (filters.DegreeCode?.length || 0) > 0 ||
    (filters.BranchCode?.length || 0) > 0 ||
    (filters.SemesterCode?.length || 0) > 0;

  const handleFilterStudents = async () => {
    setListError("");
    setFilteredStudents([]);
    setAllFilteredStudents([]);
    setIsCollapsed(false);

    if (!logincampusCode) return;
    if (!anyFilterSelected) {
      setListError("Select at least one dropdown filter to load students.");
      return;
    }

    try {
      setLoadingList(true);

      const params = { CampusCode: logincampusCode };
      if (filters.SubCampusCode.length) {params.SubCampusCode = filters.SubCampusCode.join(",");}
      if (filters.DegreeCode.length) {params.DegreeCode = filters.DegreeCode.join(",");}
      if (filters.BranchCode.length) {params.BranchCode = filters.BranchCode.join(",");}
      if (filters.SemesterCode.length) {params.SemesterCode = filters.SemesterCode.join(",");}

      const { data } = await axios.get(`${API_URL}/api/dataReportStudentMarksList`, {
        ...authHeader,
        params,
      });

      const arr = Array.isArray(data) ? data : [];
      setFilteredStudents(arr);
      setAllFilteredStudents(arr);

      if (!arr.length) setListError("No students found for the selected filters.");
    } catch (e) {
      console.error("Error loading students list:", e);
      setListError("Failed to load students. Please try again.");
    } finally {
      setLoadingList(false);
    }
  };

  // click student row to collapse/expand + show marks
  const handleStudentRowClick = (s) => {
    if (isCollapsed && filteredStudents.length === 1 && filteredStudents[0].StudentID === s.StudentID) {
      setFilteredStudents(allFilteredStudents);
      setIsCollapsed(false);
      return;
    }

    setFilteredStudents([s]);
    setIsCollapsed(true);

    setSelectedStudent(s);
    fetchStudentMarks(s.StudentID);
  };

  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        {/* ✅ Orange header bar */}
        <Box sx={headerSx}>
          <Box>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 900, lineHeight: 1.2 }}>
              Student Marks Report
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
              Filter students and view semester-wise marks
            </Typography>
          </Box>
        </Box>

        <Box sx={bodySx}>
          {/* Messages */}
          {error && (
            <Alert
              severity="error"
              onClose={() => setError("")}
              sx={{
                mb: 2,
                borderRadius: 2,
                border: "1px solid rgba(244,67,54,0.25)",
                backgroundColor: "rgba(244,67,54,0.04)",
              }}
            >
              {error}
            </Alert>
          )}

          {success && (
            <Snackbar open autoHideDuration={3000} onClose={() => setSuccess(false)}>
              <Alert severity="success" sx={{ borderRadius: 2 }} onClose={() => setSuccess(false)}>
                Operation successful!
              </Alert>
            </Snackbar>
          )}

          {/* ✅ Filters Section (accent stripe) */}
          <Paper variant="outlined" sx={sectionSx}>
            <Box sx={{ pl: 1 }}>
              <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
                Filters
              </Typography>
              <Divider sx={{ mb: 2, borderColor: "rgba(25,118,210,0.12)" }} />

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(5, 1fr)" },
                  gap: 1,
                  alignItems: "start",
                }}
              >
                {/* SubCampusCode */}
                <Autocomplete
                  multiple
                  options={options.SubCampusCode}
                  value={filters.SubCampusCode}
                  onChange={(_, value) => setFilters((p) => ({ ...p, SubCampusCode: value }))}
                  disableCloseOnSelect
                  filterSelectedOptions
                  openOnFocus
                  loading={loadingLookups}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="SubCampusCode(s)"
                      size="small"
                      sx={inputSx}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingLookups ? <CircularProgress size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />

                {/* DegreeCode */}
                <Autocomplete
                  multiple
                  options={options.DegreeCode}
                  value={filters.DegreeCode}
                  onChange={(_, value) => setFilters((p) => ({ ...p, DegreeCode: value }))}
                  disableCloseOnSelect
                  filterSelectedOptions
                  openOnFocus
                  loading={loadingLookups}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="DegreeCode(s)"
                      size="small"
                      sx={inputSx}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingLookups ? <CircularProgress size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />

                {/* BranchCode */}
                <Autocomplete
                  multiple
                  options={options.BranchCode}
                  value={filters.BranchCode}
                  onChange={(_, value) => setFilters((p) => ({ ...p, BranchCode: value }))}
                  disableCloseOnSelect
                  filterSelectedOptions
                  openOnFocus
                  loading={loadingLookups}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="BranchCode(s)"
                      size="small"
                      sx={inputSx}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingLookups ? <CircularProgress size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />

                {/* SemesterCode */}
                <Autocomplete
                  multiple
                  options={options.SemesterCode}
                  value={filters.SemesterCode}
                  onChange={(_, value) => setFilters((p) => ({ ...p, SemesterCode: value.map(String) }))}
                  disableCloseOnSelect
                  filterSelectedOptions
                  openOnFocus
                  loading={loadingLookups}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="SemesterCode(s)"
                      size="small"
                      sx={inputSx}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingLookups ? <CircularProgress size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />

                {/* Filter button + error */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.6 }}>
                  <Button
                    variant="contained"
                    onClick={handleFilterStudents}
                    disabled={loadingList}
                    sx={{ ...primaryBtnSx, height: 36 }}
                  >
                    {loadingList ? "Loading…" : "Filter Students"}
                  </Button>

                  {listError && (
                    <Typography variant="body2" sx={{ color: "error.main" }}>
                      {listError}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          </Paper>

          {/* Students list loading */}
          {loadingList && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress />
            </Box>
          )}

          {/* ✅ Search Section */}
          <Paper variant="outlined" sx={{ ...surfaceSx, p: 2, mt: 2 }}>
            <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
              Search
            </Typography>
            <Divider sx={{ mb: 2, borderColor: "rgba(25,118,210,0.12)" }} />

            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <Autocomplete
                freeSolo
                options={studentList}
                getOptionLabel={(option) => (option.FullName ? `${option.FullName} (${option.StudentID})` : "")}
                onInputChange={(_, value) => setSearchTerm(value)}
                onChange={(_, value) => setSelectedStudent(value)}
                renderInput={(params) => (
                  <TextField {...params} label="Search by Name or StudentID" size="small" sx={inputSx} fullWidth />
                )}
                sx={{ flex: 1, minWidth: 280 }}
              />

              <Button variant="contained" onClick={handleSearch} sx={{ ...primaryBtnSx, height: 36, minWidth: 120 }}>
                Search
              </Button>
            </Box>
          </Paper>

          {/* ✅ Students Matching Filters */}
          {filteredStudents.length > 0 && (
            <Paper variant="outlined" sx={{ ...surfaceSx, mt: 2 }}>
              <Box sx={{ p: 2 }}>
                <Typography sx={{ mb: 1, fontWeight: 900, color: "rgba(25,118,210,0.95)", textAlign: "left" }}>
                  Students Matching Filters {isCollapsed ? "(collapsed)" : ""}
                </Typography>

                <TableContainer sx={{ ...tableWrapSx }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={theadRowSx}>
                        <TableCell sx={{ width: 60 }}>#</TableCell>
                        <TableCell>StudentID</TableCell>
                        <TableCell>Full Name</TableCell>
                        <TableCell>SubCampus</TableCell>
                        <TableCell>Degree</TableCell>
                        <TableCell>Branch</TableCell>
                        <TableCell>Semester</TableCell>
                        <TableCell>CGPA</TableCell>
                        <TableCell>Percentage</TableCell>
                        <TableCell>NoOfBacklogs</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {filteredStudents.map((s, idx) => (
                        <TableRow
                          key={`${s.StudentID}-${idx}`}
                          sx={{
                            ...zebraRowSx(idx),
                            cursor: "pointer",
                          }}
                          onClick={() => handleStudentRowClick(s)}
                        >
                          <TableCell>{idx + 1}</TableCell>
                          <TableCell>{s.StudentID}</TableCell>
                          <TableCell>{s.FullName}</TableCell>
                          <TableCell>{s.SubCampusCode}</TableCell>
                          <TableCell>{s.DegreeCode}</TableCell>
                          <TableCell>{s.BranchCode}</TableCell>
                          <TableCell>{s.SemesterCode}</TableCell>
                          <TableCell>{s.CGPA}</TableCell>
                          <TableCell>{Number(s.Percentage || 0)}%</TableCell>
                          <TableCell>{Number(s.BacklogCount || 0)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Paper>
          )}

          {/* ✅ Student Details (accent stripe) */}
          {studentDetails && (
            <Paper variant="outlined" sx={{ ...sectionSx, mt: 2 }}>
              <Box sx={{ pl: 1 }}>
                <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
                  Student Details
                </Typography>
                <Divider sx={{ mb: 2, borderColor: "rgba(25,118,210,0.12)" }} />

                <Table size="small" sx={{ width: "100%" }}>
                  <TableBody>
                    <TableRow sx={zebraRowSx(0)}>
                      <TableCell sx={{ fontWeight: 900, width: 180 }}>Student ID</TableCell>
                      <TableCell>{studentDetails.StudentID}</TableCell>
                      <TableCell sx={{ fontWeight: 900, width: 180 }}>Student Name</TableCell>
                      <TableCell>{studentDetails.FullName}</TableCell>
                    </TableRow>

                    <TableRow sx={zebraRowSx(1)}>
                      <TableCell sx={{ fontWeight: 900 }}>Campus Name</TableCell>
                      <TableCell>{studentDetails.CampusName}</TableCell>
                      <TableCell sx={{ fontWeight: 900 }}>Degree Name</TableCell>
                      <TableCell>{studentDetails.DegreeName}</TableCell>
                    </TableRow>

                    <TableRow sx={zebraRowSx(2)}>
                      <TableCell sx={{ fontWeight: 900 }}>Branch Name</TableCell>
                      <TableCell>{studentDetails.BranchName}</TableCell>
                      <TableCell />
                      <TableCell />
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>
            </Paper>
          )}

          {/* ✅ Marks by Semester */}
          {Object.entries(marksBySemester).map(([semester, marks]) => {
            const totalCredit = marks.reduce((sum, item) => sum + (item.Credit || 0), 0);
            const GPA = new Set();
            marks.forEach((item) => {
              if (item.GPA) GPA.add(item.GPA);
            });

            return (
              <TableContainer key={semester} component={Paper} sx={{ ...tableWrapSx, mt: 2 }}>
                <Table size="small" sx={{ width: "100%" }}>
                  <TableHead>
                    <TableRow sx={subHeaderRowSx}>
                      <TableCell colSpan={6} align="center" sx={{ fontSize: 16 }}>
                        Semester {semester}
                      </TableCell>
                    </TableRow>

                    <TableRow sx={theadRowSx}>
                      <TableCell>Subject Code</TableCell>
                      <TableCell>Subject Name</TableCell>
                      <TableCell>Credits</TableCell>
                      <TableCell>Grade</TableCell>
                      <TableCell>Grade Point</TableCell>
                      <TableCell>Pass Year</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {marks.map((item, index) => (
                      <TableRow key={index} sx={zebraRowSx(index)}>
                        <TableCell>{item.SubjectCode}</TableCell>
                        <TableCell>{item.SubjectName}</TableCell>
                        <TableCell>{item.Credit}</TableCell>
                        <TableCell>{item.Grade}</TableCell>
                        <TableCell>{item.GradePoint}</TableCell>
                        <TableCell>{item.Year}</TableCell>
                      </TableRow>
                    ))}

                    <TableRow sx={{ backgroundColor: "rgba(25,118,210,0.06)" }}>
                      <TableCell colSpan={2} align="right" sx={{ fontWeight: 900, color: "rgba(25,118,210,0.95)" }}>
                        Total Credits: {totalCredit}
                      </TableCell>
                      <TableCell colSpan={2} align="right" sx={{ fontWeight: 900, color: "rgba(25,118,210,0.95)" }}>
                        SGPA: {[...GPA].join(", ")}
                      </TableCell>
                      <TableCell colSpan={2} />
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            );
          })}

          {/* ✅ Overall Totals band */}
          {studentDetails && (
            <Paper variant="outlined" sx={{ ...surfaceSx, mt: 2, overflow: "hidden" }}>
              <TableContainer>
                <Table size="small">
                  <TableBody>
                    <TableRow
                      sx={{
                        backgroundColor: "rgba(25,118,210,0.95)",
                        "& td": { color: "#fff", fontWeight: 900, py: 1.2, borderBottom: "none" },
                      }}
                    >
                      <TableCell align="right">Total Credits: {studentDetails.TotalCredits}</TableCell>
                      <TableCell align="center">CGPA: {studentDetails.CGPA}</TableCell>
                      <TableCell align="left">Percentage: {studentDetails.Percentage}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default ReportStudentMarks;
